import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  FaTrash,
  FaSearch,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUserCheck,
  FaInbox,
} from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import { getAllBookingRequests } from "../redux/actions/contents";
import PageName from "../Components/global/PageName";

export default function BookingRequests() {
  const dispatch = useDispatch();
  const { bookingRequestsList, bookingRequestsCount, loading: bookingsLoading } = useSelector(
    (state) => state.content
  );
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sync, setSync] = useState(0);

  useEffect(() => {
    dispatch(getAllBookingRequests(pageNo, pageSize, searchParams, statusFilter));
  }, [dispatch, pageNo, pageSize, searchParams, statusFilter, sync]);

  const handleDelete = async (id) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this booking request?"
    );
    if (!userRes) return;

    try {
      setLoading(true);
      const { data: res } = await axios.delete(
        `${server}/bookingRequest/deleteById`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            bookingRequestId: id,
          },
        }
      );
      toast.success(res?.message || "Booking request deleted.");
      setSync((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      setLoading(true);
      const { data: res } = await axios.put(
        `${server}/bookingRequest/updateStatus`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            bookingRequestId: id,
            status: newStatus,
          },
        }
      );
      toast.success(res?.message || `Status updated to ${newStatus}`);
      setSync((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "N/A";
    if (!isNaN(dateStr)) {
      return new Date(Number(dateStr)).toDateString();
    }
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toDateString();
  };

  const getStatusBadge = (status) => {
    const s = (status || "new").toLowerCase();
    if (s === "accepted") {
      return (
        <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-[0.65rem] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
          <FaCheckCircle size={10} /> Accepted
        </span>
      );
    } else if (s === "rejected") {
      return (
        <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-800 text-[0.65rem] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
          <FaTimesCircle size={10} /> Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[0.65rem] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
        <FaClock size={10} /> New Request
      </span>
    );
  };

  // KPI calculations
  const totalCount = bookingRequestsCount || 0;
  const newCount = bookingRequestsList?.filter(
    (b) => !b.status || b.status.toLowerCase() === "new"
  ).length || 0;
  const acceptedCount = bookingRequestsList?.filter(
    (b) => b.status && b.status.toLowerCase() === "accepted"
  ).length || 0;

  return (
    <div className="min-h-screen pb-12">
      <PageName
        title="Bridal Booking Console"
        subTitle="Review and manage client makeover schedule requests."
      />

      <div className="max-w-7xl mx-auto px-4 mt-6">
        {/* KPI Metrics Widgets */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Total Bookings
              </p>
              <h3 className="text-2xl font-extrabold text-slate-900 mt-1">
                {totalCount}
              </h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center text-xl shadow-xs">
              <FaInbox />
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Pending Requests
              </p>
              <h3 className="text-2xl font-extrabold text-amber-600 mt-1">
                {newCount}
              </h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-xl shadow-xs">
              <FaClock />
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Accepted Makeovers
              </p>
              <h3 className="text-2xl font-extrabold text-emerald-600 mt-1">
                {acceptedCount}
              </h3>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-xl shadow-xs">
              <FaUserCheck />
            </div>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div className="bg-white p-4 rounded-2xl shadow-2xs border border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
          {/* Status Tabs */}
          <div className="flex gap-2 flex-wrap">
            {["All", "new", "accepted", "rejected"].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setStatusFilter(tab);
                  setPageNo(1);
                }}
                className={`px-4 py-2 text-xs font-bold rounded-xl capitalize transition-all ${
                  statusFilter === tab
                    ? "bg-slate-900 text-white shadow-xs"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab === "new" ? "New Requests" : tab}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-80">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
            <input
              type="text"
              placeholder="Search by name, mobile, location..."
              value={searchParams}
              onChange={(e) => {
                setSearchParams(e.target.value);
                setPageNo(1);
              }}
              className="w-full pl-9 pr-4 py-2 text-xs border border-slate-300 rounded-xl focus:outline-none focus:border-amber-600 bg-white"
            />
          </div>
        </div>

        {/* Requests Table */}
        <div className="overflow-x-auto rounded-2xl shadow-xs border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
              <tr>
                <th className="px-5 py-4 text-left">#</th>
                <th className="px-5 py-4 text-left">Bride Details</th>
                <th className="px-5 py-4 text-left">Service & Event</th>
                <th className="px-5 py-4 text-left">Date & Venue</th>
                <th className="px-5 py-4 text-left">Add-Ons & Notes</th>
                <th className="px-5 py-4 text-center">Status</th>
                <th className="px-5 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {bookingRequestsList?.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-slate-400">
                    No booking requests found matching your filter criteria.
                  </td>
                </tr>
              ) : (
                bookingRequestsList?.map((item, index) => (
                  <motion.tr
                    key={item.id || index}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="hover:bg-amber-50/40 transition-colors"
                  >
                    <td className="px-5 py-4 text-slate-400 font-bold text-left">
                      {((pageNo - 1) * pageSize + index + 1).toString().padStart(2, "0")}.
                    </td>

                    {/* Bride Details */}
                    <td className="px-5 py-4 text-left">
                      <div className="font-bold text-slate-900 capitalize text-sm">
                        {item.name || "Anonymous Bride"}
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-600 font-medium mt-1">
                        <FaPhoneAlt size={10} className="text-amber-600" />
                        <span>{item.mobile || "N/A"}</span>
                      </div>
                      {item.email && (
                        <div className="flex items-center gap-1.5 text-slate-500 mt-0.5">
                          <FaEnvelope size={10} className="text-slate-400" />
                          <span>{item.email}</span>
                        </div>
                      )}
                    </td>

                    {/* Service & Event */}
                    <td className="px-5 py-4 text-left">
                      <div className="font-bold text-amber-800">
                        {item.service || "Standard Makeover"}
                      </div>
                      <div className="text-slate-600 mt-0.5">
                        Event: <span className="font-semibold text-slate-800">{item.event || "Wedding Ceremony"}</span>
                      </div>
                    </td>

                    {/* Date & Venue */}
                    <td className="px-5 py-4 text-left">
                      <div className="flex items-center gap-1.5 text-slate-800 font-bold">
                        <FaCalendarAlt size={11} className="text-amber-600" />
                        <span>{formatDateDisplay(item.bookingDate)}</span>
                      </div>
                      <div className="flex items-start gap-1.5 text-slate-600 mt-1 max-w-xs">
                        <FaMapMarkerAlt size={11} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="truncate">{item.location || "Location pending"}</span>
                      </div>
                    </td>

                    {/* Requirements */}
                    <td className="px-5 py-4 text-left">
                      <div className="flex flex-wrap gap-1.5 mb-1">
                        {item.hairstyling !== false && (
                          <span className="bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase">
                            + Hairstyling
                          </span>
                        )}
                        {item.draping !== false && (
                          <span className="bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full text-[0.62rem] font-bold uppercase">
                            + Draping
                          </span>
                        )}
                      </div>
                      {(item.additionalNotes || item.remarks) && (
                        <p className="text-slate-500 italic text-[0.7rem] max-w-xs truncate mt-1" title={item.additionalNotes || item.remarks}>
                          "{item.additionalNotes || item.remarks}"
                        </p>
                      )}
                    </td>

                    {/* Status Badge */}
                    <td className="px-5 py-4 text-center">
                      {getStatusBadge(item.status)}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <select
                          value={(item.status || "new").toLowerCase()}
                          onChange={(e) => handleUpdateStatus(item.id, e.target.value)}
                          className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-600 bg-white font-medium text-slate-800 shadow-2xs"
                        >
                          <option value="new">New</option>
                          <option value="accepted">Accept</option>
                          <option value="rejected">Reject</option>
                        </select>

                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Delete Booking"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalCount > pageSize && (
          <div className="flex justify-between items-center mt-6 bg-white px-5 py-3.5 rounded-xl border border-slate-200 text-xs shadow-2xs">
            <span className="text-slate-500 font-medium">
              Showing <span className="font-bold text-slate-800">{(pageNo - 1) * pageSize + 1}</span> to{" "}
              <span className="font-bold text-slate-800">{Math.min(pageNo * pageSize, totalCount)}</span> of{" "}
              <span className="font-bold text-slate-800">{totalCount}</span> requests
            </span>
            <div className="flex gap-2">
              <button
                disabled={pageNo === 1}
                onClick={() => setPageNo((p) => p - 1)}
                className="px-3.5 py-1.5 border border-slate-300 rounded-lg text-slate-700 font-semibold disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={pageNo * pageSize >= totalCount}
                onClick={() => setPageNo((p) => p + 1)}
                className="px-3.5 py-1.5 border border-slate-300 rounded-lg text-slate-700 font-semibold disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {(loading || bookingsLoading) && <Loading />}
    </div>
  );
}
